import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { CalendarOff, Plane, Thermometer, Lock, Umbrella, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, apiError } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Page";

export default function Abwesenheiten() {
  const [vacations, setVacations] = useState([]);
  const [sick, setSick] = useState([]);
  const [ov, setOv] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [form, setForm] = useState({ type: "block", start_date: "", end_date: "", note: "" });

  const loadVac = () => api.get("/vacations").then((r) => setVacations((r.data || []).filter((v) => v.status === "approved")));
  const loadOv = () => api.get("/admin/vacation-overview").then((r) => setOv(r.data));
  const loadBlocks = () => api.get("/vacation-blocks").then((r) => setBlocks(r.data || []));

  useEffect(() => {
    loadVac();
    api.get("/sick-leaves").then((r) => setSick(r.data || []));
    loadOv();
    loadBlocks();
  }, []);

  const createBlock = async () => {
    if (!form.start_date || !form.end_date) return toast.error("Bitte Zeitraum wählen");
    try {
      const r = await api.post("/vacation-blocks", form);
      if (form.type === "company_holiday") {
        toast.success(`Betriebsurlaub eingetragen – ${r.data.created_vacations} Mitarbeiter automatisch verbucht`);
        loadVac();
        loadOv();
      } else {
        toast.success("Urlaubssperre eingetragen");
      }
      setForm({ type: "block", start_date: "", end_date: "", note: "" });
      loadBlocks();
    } catch (e) { toast.error(apiError(e)); }
  };

  const delBlock = async (b) => {
    try {
      const r = await api.delete(`/vacation-blocks/${b.id}`);
      toast.success(b.type === "company_holiday" ? `Betriebsurlaub entfernt (${r.data.removed_vacations} Einträge gelöscht)` : "Urlaubssperre entfernt");
      loadBlocks();
      if (b.type === "company_holiday") { loadVac(); loadOv(); }
    } catch (e) { toast.error(apiError(e)); }
  };

  const Item = ({ icon: Icon, name, from, to, note, cls, testid }) => (
    <Card className={`p-4 border rounded-xl flex items-center justify-between ${cls}`} data-testid={testid}>
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5" />
        <div>
          <div className="font-medium text-slate-900">{name}</div>
          <div className="text-sm text-slate-500">{fmtDate(from)} – {fmtDate(to)}{note ? ` · ${note}` : ""}</div>
        </div>
      </div>
    </Card>
  );

  return (
    <div>
      <PageHeader title="Abwesenheiten" subtitle="Genehmigte Urlaube, Krankmeldungen, Urlaubssperren & Betriebsurlaub." testid="abwesenheiten-header" />
      <Tabs defaultValue="uebersicht">
        <TabsList className="h-auto p-1 bg-slate-100 rounded-lg mb-4">
          <TabsTrigger value="uebersicht" data-testid="tab-abw-uebersicht">Urlaubsübersicht</TabsTrigger>
          <TabsTrigger value="urlaub" data-testid="tab-abw-urlaub">Urlaub ({vacations.length})</TabsTrigger>
          <TabsTrigger value="krank" data-testid="tab-abw-krank">Krank ({sick.length})</TabsTrigger>
          <TabsTrigger value="sperren" data-testid="tab-abw-sperren">Sperren & Betriebsurlaub ({blocks.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="uebersicht">
          <Card className="border-slate-200 rounded-xl overflow-hidden" data-testid="vacation-overview">
            {!ov || ov.rows.length === 0 ? (
              <EmptyState icon={Plane} title="Keine Mitarbeiter" testid="abw-ov-empty" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mitarbeiter</TableHead>
                    <TableHead>Anspruch {ov.year}</TableHead>
                    {ov.carryover_enabled && <TableHead>Übertrag</TableHead>}
                    <TableHead>Genommen</TableHead>
                    <TableHead>In Bearbeitung</TableHead>
                    <TableHead>Offen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ov.rows.map((r) => (
                    <TableRow key={r.user_id} data-testid={`vac-ov-${r.user_id}`} className={r.active ? "" : "opacity-50"}>
                      <TableCell className="font-medium">{r.user_name}</TableCell>
                      <TableCell>{r.total}</TableCell>
                      {ov.carryover_enabled && <TableCell>{r.carryover || 0}</TableCell>}
                      <TableCell>{r.taken}</TableCell>
                      <TableCell className="text-purple-600">{r.pending}</TableCell>
                      <TableCell className="font-semibold text-emerald-600">{r.remaining}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="urlaub">
          <div className="grid gap-3">
            {vacations.length === 0 ? (
              <Card className="border-slate-200 rounded-xl"><EmptyState icon={CalendarOff} title="Keine genehmigten Urlaube" testid="abw-urlaub-empty" /></Card>
            ) : vacations.map((v) => (
              <Item key={v.id} icon={Plane} name={v.user_name} from={v.start_date} to={v.end_date} note={`${v.half_day ? `Halber Tag${v.half_day_period ? ` (${v.half_day_period === "vormittag" ? "Vormittag" : "Nachmittag"})` : ""}${v.note ? " · " : ""}` : ""}${v.note || ""}`} cls="bg-blue-50 border-blue-200" testid={`abw-vacation-${v.id}`} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="krank">
          <div className="grid gap-3">
            {sick.length === 0 ? (
              <Card className="border-slate-200 rounded-xl"><EmptyState icon={CalendarOff} title="Keine Krankmeldungen" testid="abw-krank-empty" /></Card>
            ) : sick.map((s) => (
              <Item key={s.id} icon={Thermometer} name={s.user_name} from={s.start_date} to={s.end_date} note={s.note} cls="bg-rose-50 border-rose-200" testid={`abw-sick-${s.id}`} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="sperren">
          <Card className="p-5 border-slate-200 rounded-xl mb-4" data-testid="block-create-card">
            <h3 className="font-semibold text-slate-900 mb-1">Neuer Eintrag</h3>
            <p className="text-xs text-slate-400 mb-4">
              <strong>Urlaubssperre:</strong> Mitarbeiter können in diesem Zeitraum keinen Urlaub beantragen.<br />
              <strong>Betriebsurlaub:</strong> Trägt allen Mitarbeitern automatisch genehmigten Urlaub ein (wird vom Anspruch abgezogen).
            </p>
            <div className="grid sm:grid-cols-4 gap-3 items-end">
              <div className="space-y-2">
                <Label>Typ</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="block-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="block">Urlaubssperre</SelectItem>
                    <SelectItem value="company_holiday">Betriebsurlaub</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Von</Label><Input type="date" data-testid="block-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div className="space-y-2"><Label>Bis</Label><Input type="date" data-testid="block-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              <div className="space-y-2"><Label>Anmerkung</Label><Input data-testid="block-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional" /></div>
            </div>
            <Button onClick={createBlock} data-testid="block-create" className="brand-bg text-white hover:opacity-90 mt-4"><Plus className="h-4 w-4 mr-2" /> Eintragen</Button>
          </Card>

          <div className="grid gap-3">
            {blocks.length === 0 ? (
              <Card className="border-slate-200 rounded-xl"><EmptyState icon={Lock} title="Keine Sperren oder Betriebsurlaube" testid="abw-blocks-empty" /></Card>
            ) : blocks.map((b) => {
              const holiday = b.type === "company_holiday";
              return (
                <Card key={b.id} className={`p-4 border rounded-xl flex items-center justify-between ${holiday ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`} data-testid={`block-${b.id}`}>
                  <div className="flex items-center gap-3">
                    {holiday ? <Umbrella className="h-5 w-5 text-amber-600" /> : <Lock className="h-5 w-5 text-slate-500" />}
                    <div>
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        {holiday ? "Betriebsurlaub" : "Urlaubssperre"}
                        <Badge variant="outline" className={holiday ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200"}>{holiday ? "auto verbucht" : "gesperrt"}</Badge>
                      </div>
                      <div className="text-sm text-slate-500">{fmtDate(b.start_date)} – {fmtDate(b.end_date)}{b.note ? ` · ${b.note}` : ""}</div>
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`block-delete-${b.id}`} className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{holiday ? "Betriebsurlaub entfernen?" : "Urlaubssperre entfernen?"}</AlertDialogTitle>
                        <AlertDialogDescription>{holiday ? "Die automatisch eingetragenen Urlaubstage der Mitarbeiter für diesen Zeitraum werden ebenfalls wieder entfernt." : "Mitarbeiter können danach in diesem Zeitraum wieder Urlaub beantragen."}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Abbrechen</AlertDialogCancel><AlertDialogAction onClick={() => delBlock(b)} className="bg-rose-600 hover:bg-rose-700">Entfernen</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
